import {
    type ReactNode,
    useContext,
    useEffect,
    type FC
} from "react";
import {
    Platform,
    View
} from "react-native";
import type INavigationMenuContentProps from "./type";
import type {
    NavigationMenuContentWebPropsType
} from "./type";
import stylesheet, {
    NAVIGATION_MENU_SKELETON_ROWS,
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../../../core/hooks";
import {
    NavigationMenuLayoutContext,
    NavigationMenuItemContext,
    useNavigationMenuContext,
    NavigationMenuContext
} from "../../context";
import type {
    NavigationMenuKeyEventType
} from "../../type";
import {
    NAVIGATION_MENU_FOCUS_RETRY_LIMIT,
    handleNavigationMenuPanelKeyDown,
    getNavigationMenuContentWidth,
    getNavigationMenuBarOffset,
    focusNavigationMenuPanel
} from "../../util";
import {
    handlePopoverTabKeyDown
} from "../../../popover/util";
import PopoverSurface from "../../../popoverSurface";
import NavigationMenuPanel from "../panel";
import Skeleton from "../../../skeleton";

const NAVIGATION_MENU_PANEL_LAYOUT = {
    isSheet: false
};

const NavigationMenuContent: FC<INavigationMenuContentProps> = ({
    customTheme: customThemeProp,
    size = "medium",
    isLoading,
    children,
    style
}) => {
    const context = useNavigationMenuContext({
        part: "Content"
    });

    const itemContext = useContext(NavigationMenuItemContext);

    const layout = useContext(NavigationMenuLayoutContext);

    const customTheme = customThemeProp ?? context?.customTheme;

    const {
        controlHeights,
        shapeConfigs,
        borders,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        direction,
        localize
    } = NCoreUIKitLocalize.useContext(context?.customLocalize);

    const isFullWidth = context?.panelBehaviour === "fullWidth";

    const isOpen = !!context && !!itemContext && context.activeValue === itemContext.value;

    const {
        skeletonText: skeletonTextDynamicStyle,
        skeletonRow: skeletonRowDynamicStyle,
        tracks: tracksDynamicStyle
    } = useStyles({
        isFullWidth,
        spaces
    });

    useEffect(() => {
        if(!isOpen || !context?.focusRequestRef.current || Platform.OS !== "web") {
            return;
        }

        let frame: number | null = null;

        let attempts = 0;

        const handleFocus = () => {
            if(focusNavigationMenuPanel({
                surfaceRef: context.surfaceRef
            })) {
                context.focusRequestRef.current = false;

                return;
            }

            attempts += 1;

            if(attempts >= NAVIGATION_MENU_FOCUS_RETRY_LIMIT) {
                context.focusRequestRef.current = false;

                return;
            }

            frame = requestAnimationFrame(handleFocus);
        };

        handleFocus();

        return () => {
            if(frame !== null) {
                cancelAnimationFrame(frame);
            }
        };
    }, [
        isOpen
    ]);

    if(!context || !itemContext) {
        return null;
    }

    const renderSkeleton = () => {
        return <View
            aria-label={localize("loading")}
            style={stylesheet.skeleton}
            aria-busy={true}
        >
            {
                NAVIGATION_MENU_SKELETON_ROWS.map((rowKey) => <View
                    style={[
                        stylesheet.skeletonRow,
                        skeletonRowDynamicStyle
                    ]}
                    key={rowKey}
                >
                    <Skeleton
                        customTheme={customTheme}
                        variant="icon"
                    />
                    <View
                        style={[
                            stylesheet.skeletonText,
                            skeletonTextDynamicStyle
                        ]}
                    >
                        <Skeleton
                            typography="bodyMediumSize"
                            customTheme={customTheme}
                            variant="text"
                            width="70%"
                        />
                        <Skeleton
                            typography="bodySmallSize"
                            customTheme={customTheme}
                            variant="text"
                        />
                    </View>
                </View>)
            }
        </View>;
    };

    const renderBody = () => {
        return isLoading ? renderSkeleton() : children;
    };

    if(layout.isSheet) {
        return <View
            style={[
                style,
                stylesheet.sheetContent
            ]}
        >
            {renderBody()}
        </View>;
    }

    const handleFocusTrigger = () => {
        context.triggerRefs.current.get(itemContext.value)?.focus();
    };

    const onKeyDown = (event: NavigationMenuKeyEventType) => {
        if(event.key === "Tab") {
            handlePopoverTabKeyDown({
                triggerRef: itemContext.anchorRef,
                surfaceRef: context.surfaceRef,
                onLeave: context.handleClose,
                event
            });

            return;
        }

        if(event.key === "Escape") {
            context.handleClose();

            handleFocusTrigger();

            return;
        }

        handleNavigationMenuPanelKeyDown({
            onFocusTrigger: handleFocusTrigger,
            surfaceRef: context.surfaceRef,
            direction,
            event
        });
    };

    const webProps: NavigationMenuContentWebPropsType = Platform.OS === "web" ? {
        onKeyDown
    } : {};

    const barOffset = getNavigationMenuBarOffset({
        size: context.size,
        controlHeights,
        borders,
        spaces
    });

    const renderProviders = (node: ReactNode) => {
        return <NavigationMenuContext.Provider
            value={context}
        >
            <NavigationMenuItemContext.Provider
                value={itemContext}
            >
                <NavigationMenuLayoutContext.Provider
                    value={NAVIGATION_MENU_PANEL_LAYOUT}
                >
                    {node}
                </NavigationMenuLayoutContext.Provider>
            </NavigationMenuItemContext.Provider>
        </NavigationMenuContext.Provider>;
    };

    const renderTracks = () => {
        return renderProviders(<View
            style={[
                stylesheet.tracks,
                tracksDynamicStyle
            ]}
        >
            {renderBody()}
        </View>);
    };

    if(isFullWidth) {
        return <NavigationMenuPanel
            ref={isOpen ? context.surfaceRef : undefined}
            onPointerLeave={context.handleScheduleClose}
            id={context.getContentId(itemContext.value)}
            onPointerEnter={context.handleCancelClose}
            onFocus={context.handleCancelClose}
            onDismiss={context.handleClose}
            anchorRef={context.navRef}
            customTheme={customTheme}
            aria-busy={isLoading}
            offset={barOffset}
            isOpen={isOpen}
            style={style}
            {...webProps}
        >
            {renderTracks()}
        </NavigationMenuPanel>;
    }

    return <PopoverSurface
        {...webProps}
        ref={isOpen ? context.surfaceRef : undefined}
        onPointerLeave={context.handleScheduleClose}
        id={context.getContentId(itemContext.value)}
        onPointerEnter={context.handleCancelClose}
        width={getNavigationMenuContentWidth({
            shapeConfigs,
            size
        })}
        offset={barOffset + spaces.spacingXs}
        onFocus={context.handleCancelClose}
        collisionPadding={spaces.spacingMd}
        anchorRef={itemContext.anchorRef}
        onDismiss={context.handleClose}
        customTheme={customTheme}
        aria-busy={isLoading}
        isOpen={isOpen}
        style={style}
        side="bottom"
        align="start"
    >
        {renderTracks()}
    </PopoverSurface>;
};
export default NavigationMenuContent;
